/* eslint-disable complexity */
import { CustomDotCmsContentlet } from '../../models/contentlet/custom-dotcms-contentlet.model';

export function parseJsonToHtml(contentlet: CustomDotCmsContentlet): string {
  let html = '';

  const addContent = (tag: string, content: string, attrs: Record<string, string> = {}) => {
    const attributes = Object.entries(attrs)
      .map(([key, value]) => `${key}="${value}"`)
      .join(' ');
    return `<${tag} ${attributes}>${content}</${tag}>`;
  };

  switch (contentlet?.type) {
    case 'heading':
      if (contentlet.content) {
        contentlet.content.forEach((element: CustomDotCmsContentlet) => {
          if (element.type === 'text') {
            const text = applyMarks(element.text, element.marks || []);
            const level = contentlet.attrs?.level || 1;
            const textAlign = contentlet.attrs?.textAlign || 'left';
            html += addContent(`h${level}`, text, { style: `text-align: ${textAlign};` });
          }
        });
      }
      break;
    case 'paragraph':
      const textAlignProperty = contentlet.attrs?.textAlign || 'left';
      if (contentlet.content) {
        const contentHtml = contentlet.content.map(parseJsonToHtml).join('');
        html += addContent('p', contentHtml, { style: `text-align: ${textAlignProperty};` });
      } else {
        // Add a line break if the paragraph is empty
        html += `<p style="text-align: ${textAlignProperty};"></p><br>`;
      }
      break;
    case 'text':
      if (contentlet.text) {
        html += applyMarks(contentlet.text, contentlet.marks || []);
      }
      break;
    case 'table':
      html += '<table class="table table-bordered">';
      if (contentlet.content) {
        contentlet.content.forEach((row: CustomDotCmsContentlet) => {
          html += parseJsonToHtml(row);
        });
      }
      html += '</table>';
      break;
    case 'tableRow':
      html += '<thead><tr>';
      if (contentlet.content) {
        contentlet.content.forEach((cell: CustomDotCmsContentlet) => {
          html += parseJsonToHtml(cell);
        });
      }
      html += '</tr></thead>';
      break;
    case 'tableHeader':
      html += '<th>';
      if (contentlet.content) {
        contentlet.content.forEach((cellContent: CustomDotCmsContentlet) => {
          html += parseJsonToHtml(cellContent);
        });
      }
      html += '</th>';
      break;
    case 'tableCell':
      html += '<td>';
      if (contentlet.content) {
        contentlet.content.forEach((cellContent: CustomDotCmsContentlet) => {
          html += parseJsonToHtml(cellContent);
        });
      }
      html += '</td>';
      break;
    case 'codeBlock':
      if (contentlet.content) {
        contentlet.content.forEach((element: CustomDotCmsContentlet) => {
          if (element.type === 'text') {
            html += `<pre><code>${element.text}</code></pre>`;
          }
        });
      }
      break;
    case 'horizontalRule':
      html += '<hr>';
      break;
    case 'blockquote':
      html += '<blockquote>';
      if (contentlet.content) {
        contentlet.content.forEach((block: CustomDotCmsContentlet) => {
          html += parseJsonToHtml(block);
        });
      }
      html += '</blockquote>';
      break;
    case 'bulletList':
      html += '<ul>';
      if (contentlet.content) {
        contentlet.content.forEach((listItem: CustomDotCmsContentlet) => {
          html += parseJsonToHtml(listItem);
        });
      }
      html += '</ul>';
      break;
    case 'orderedList':
      const start = contentlet.attrs?.start || 1;
      html += `<ol start="${start}">`;
      if (contentlet.content) {
        contentlet.content.forEach((listItem: CustomDotCmsContentlet) => {
          html += parseJsonToHtml(listItem);
        });
      }
      html += '</ol>';
      break;
    case 'listItem':
      const textAlign = contentlet.attrs?.textAlign || 'left';
      html += `<li style="text-align: ${textAlign};">`;
      if (contentlet.content) {
        contentlet.content.forEach((item: CustomDotCmsContentlet) => {
          html += parseJsonToHtml(item);
        });
      }
      html += '</li>';
      break;
    default:
      if (contentlet.content) {
        contentlet.content.forEach((childElement: CustomDotCmsContentlet) => {
          html += parseJsonToHtml(childElement);
        });
      }
      break;
  }

  return html;
}

export function applyMarks(text: CustomDotCmsContentlet, marks: CustomDotCmsContentlet[]): string {
  let markedText = text as unknown as string;
  marks.forEach(mark => {
    switch (mark.type) {
      case 'underline':
        markedText = `<u>${markedText}</u>`;
        break;
      case 'bold':
        markedText = `<strong>${markedText}</strong>`;
        break;
      case 'italic':
        markedText = `<i>${markedText}</i>`;
        break;
      case 'strike':
        markedText = `<s>${markedText}</s>`;
        break;
      case 'subscript':
        markedText = `<sub>${markedText}</sub>`;
        break;
      case 'superscript':
        markedText = `<sup>${markedText}</sup>`;
        break;
      case 'link':
        const href = mark.attrs?.href || '#';
        const target = mark.attrs?.target || '_self';
        const rel = mark.attrs?.rel || '';
        markedText = `<a href="${href}" target="${target}" rel="${rel}">${markedText}</a>`;
        break;
    }
  });
  return markedText;
}
